import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Calendar, Clock, Users, TrendingUp, Zap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';

const ScheduleFeature = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Zap,
      title: 'AI-Powered Auto-Scheduling',
      description: 'Let artificial intelligence create optimal schedules automatically based on employee availability, skills, Employee laws, and business demands.'
    },
    {
      icon: Calendar,
      title: 'Drag-and-Drop Interface',
      description: 'Intuitive visual scheduling with drag-and-drop functionality makes schedule adjustments quick and effortless.'
    },
    {
      icon: Clock,
      title: 'Shift Templates',
      description: 'Save time with reusable shift templates for recurring schedules. Copy previous weeks or create custom patterns.'
    },
    {
      icon: Users,
      title: 'Multi-Location Support',
      description: 'Manage schedules across unlimited locations and departments from a single unified dashboard.'
    },
    {
      icon: TrendingUp,
      title: 'Employee Cost Forecasting',
      description: 'See projected Employee costs in real-time as you build schedules. Stay within budget effortlessly.'
    },
    {
      icon: CheckCircle2,
      title: 'Conflict Detection',
      description: 'Automatic detection of double-bookings, overtime violations, and compliance issues before publishing.'
    }
  ];

  const benefits = [
    '90% faster schedule creation compared to manual methods',
    '99.8% conflict-free schedules with AI validation',
    'Automatic Employee law compliance checking',
    'Real-time schedule updates pushed to mobile devices',
    'Employee self-service shift trading and swapping',
    'Integration with time tracking and payroll systems',
    'Shift availability requests from employees',
    'Open shift posting for voluntary pickup',
    'Schedule templates for recurring patterns',
    'Historical data analysis for optimal staffing'
  ];

  const useCases = [
    {
      title: 'Healthcare Facilities',
      description: 'Ensure proper nurse-to-patient ratios, track certifications, manage on-call schedules, and maintain 24/7 coverage across all departments.'
    },
    {
      title: 'Restaurants & Hospitality',
      description: 'Schedule based on reservations and foot traffic predictions. Handle split shifts, manage tips distribution, and optimize Employee for peak hours.'
    },
    {
      title: 'Retail Stores',
      description: 'Align staffing with sales patterns and promotional events. Schedule seasonal workers efficiently and ensure adequate floor coverage.'
    },
    {
      title: 'Manufacturing Plants',
      description: 'Coordinate complex multi-shift rotations, track equipment certifications, plan for production quotas, and manage union rules.'
    }
  ];

  return (
    <>
      <LandingNav />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Intelligent Schedule Management
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto">
              Create perfect schedules in minutes, not hours. AI-powered scheduling that saves 10+ hours per week while optimizing coverage and reducing costs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/signup')}
                className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all flex items-center justify-center"
              >
                Start Free Trial
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="bg-blue-500/20 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-500/30 transition-all"
              >
                Schedule Demo
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Screenshot Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
            <img 
              src="/Schedule Page.png" 
              alt="Intelligent Schedule Management Interface"
              className="w-full h-auto rounded-xl"
            />
          </div>
          <div className="grid grid-cols-3 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">90%</div>
              <div className="text-gray-600">Faster Scheduling</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">99.8%</div>
              <div className="text-gray-600">Conflict-Free</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">4.9/5</div>
              <div className="text-gray-600">User Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Powerful Scheduling Features
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Everything you need to create, manage, and optimize employee schedules efficiently.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-100 hover:shadow-xl transition-all"
                >
                  <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Key Benefits of Intelligent Scheduling
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Transform your workforce planning with AI-powered automation that eliminates manual scheduling headaches while ensuring optimal coverage and employee satisfaction.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Set Your Parameters</h4>
                    <p className="text-gray-600">Define business hours, shift lengths, staffing requirements, and employee availability.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">AI Creates Schedule</h4>
                    <p className="text-gray-600">Our algorithm analyzes all factors and generates an optimal schedule in seconds.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Review & Adjust</h4>
                    <p className="text-gray-600">Make manual tweaks with drag-and-drop if needed. AI prevents conflicts.</p>
                  </div>
                </div>
                <div className="flex">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Publish & Notify</h4>
                    <p className="text-gray-600">Send schedule to all employees instantly via mobile app and email.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
            Perfect for Every Industry
          </h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            Intelligent scheduling adapts to your industry's unique requirements and challenges.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-8 border border-gray-200 hover:shadow-xl transition-all"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.title}</h3>
                <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Scheduling?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of organizations saving 10+ hours weekly with intelligent scheduling.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg shadow-2xl hover:shadow-3xl transition-all"
            >
              Start Free 14-Day Trial
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/10 transition-all"
            >
              Schedule a Demo
            </button>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default ScheduleFeature;
